import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDispatch } from "react-redux";

import { showSuccessToast } from "app/actions/toast_actions";
import { swSettings } from "app/common/services/swSettings";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { keywordGroupsTransforms } from "app/services/keyword-groups/utils/keywordGroupsTransforms";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { translate } from "app/filters/i18nFilter";
import { getListOfNewKeywords } from "app/pages/activation/components/Topics/Modal/Flow/Advanced/helpers";
import { keywordModalTexts } from "app/pages/activation/components/Topics/Modal/Flow/Advanced/texts";
import type { KeywordItemSchema } from "app/pages/activation/components/Topics/Modal/Flow/Advanced/types";
import { AddedKWType } from "app/pages/activation/components/Topics/Modal/Flow/Advanced/types";
import { activationTracking } from "app/pages/activation/services/tracking";

import { allowedRegex, normalize } from "../../components/Topics/Modal/Flow/Collision/constants";
import { ALLOWED_CHARS, NAME_USED } from "../../components/Topics/Modal/Flow/Collision/texts";
import type { KWGroupSchema } from "../../components/Topics/types";
import { ActivationComponentAssets } from "../../helpers/ActivationAssets";
import { getUniqKwNames } from "../../helpers/home/keywords";

export type KeywordsGroupsSchema = {
    sharedKWList: KWGroupSchema[];
    ownKWList: KWGroupSchema[];
    isLoading: boolean;
};

//FIXME Requires refactoring
const useKWModalUpdater = () => {
    const keywordsLimit = ActivationComponentAssets.maxAmountOfKeywords;
    const [titleError, setTitleError] = useState("");

    const debounceRef = useRef(null);

    const dispatch = useDispatch();
    const track = activationTracking.home;

    const [queryKeyword, setQueryKeyword] = useState("");
    const [keywordsList, setKeywordList] = useState<Array<KeywordItemSchema>>([]);
    const [title, setTitle] = useState("");

    const savedKeywords = useRef<Array<KeywordItemSchema>>([]);
    const removedKeywordsList = useRef<Array<KeywordItemSchema>>([]);

    const selectedKeywords = useRef<Array<string>>([]);

    const [keywordsGroups, setKeywordsGroups] = useState<KeywordsGroupsSchema>({
        sharedKWList: [],
        ownKWList: [],
        isLoading: false,
    });

    useEffect(() => {
        const getKeywordsGroups = async () => {
            setKeywordsGroups({ sharedKWList: [], ownKWList: [], isLoading: true });

            const userId = swSettings.user.id;

            await keywordGroupsQueryClient.invalidateAll();

            const { userGroups, sharedGroups } = await keywordGroupsQueryClient.getAllGroups();
            const dropdownGroups = keywordGroupsTransforms.toDropDown(userGroups);
            const ownKwIds = [];

            const sharedGroupsDropdown = keywordGroupsTransforms.toDropDown(sharedGroups);

            const sharedKWList = [...sharedGroupsDropdown];
            const ownKWList = [];

            dropdownGroups.forEach((keyword) => {
                if (keyword.shared && Number(keyword.userId) !== userId) {
                    sharedKWList.push(keyword);

                    return;
                }

                ownKWList.push(keyword);
                ownKwIds.push(keyword.id);
            });

            const filteredSharedKWList = sharedKWList.filter((keyword) => !ownKwIds.includes(keyword.id));

            setKeywordsGroups({ sharedKWList: filteredSharedKWList, ownKWList, isLoading: false });
        };

        getKeywordsGroups();
    }, []);

    const existingNamesSet = useMemo(() => getUniqKwNames(keywordsGroups), [keywordsGroups]);

    const onChangeTitle = useCallback(
        (e: React.ChangeEvent<HTMLInputElement> | string) => {
            const text = typeof e === "string" ? e : e.target.value;

            setTitle(text);

            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            debounceRef.current = setTimeout(() => {
                const normal = normalize(text);

                if (!allowedRegex.test(text)) {
                    setTitleError(translate(ALLOWED_CHARS));
                } else if (normal && existingNamesSet.has(normal)) {
                    setTitleError(translate(NAME_USED));
                } else {
                    setTitleError(translate(""));
                }
            }, 300);
        },
        [existingNamesSet],
    );

    const showToast = (text: string, linkText?: string, onClick?: () => void) => {
        const { toast } = dispatch(
            showSuccessToast(
                getToastItemComponent({
                    text: text,
                    linkText: linkText,
                    onClick,
                }),
            ),
        );

        return toast;
    };

    const updateSavedKeywords = (keywords: Array<KeywordItemSchema>) => {
        savedKeywords.current = keywords;
    };

    const clearSelection = () => {
        selectedKeywords.current = [];

        setKeywordList((keywords) =>
            keywords.map((keyword) => (keyword.isSelected ? { ...keyword, isSelected: false } : keyword)),
        );
    };

    const updateKeywordsList = (newKeywords: Array<string>) => {
        const { newKeywordsAdded, isNewKWReduced, initialNewKWListCount } = getListOfNewKeywords(
            newKeywords,
            keywordsList,
        );

        if (isNewKWReduced) {
            showToast(
                translate(keywordModalTexts.limitation, {
                    added: newKeywordsAdded.length,
                    count: initialNewKWListCount,
                }),
            );
        }

        if (!newKeywordsAdded.length) {
            return;
        }

        const updatedKeyword = [...newKeywordsAdded, ...keywordsList];

        updateSavedKeywords(updatedKeyword);
        setKeywordList(updatedKeyword);

        if (!title) {
            onChangeTitle(updatedKeyword[0].value);
        }
    };

    const onAdd = () => {
        if (!queryKeyword.trim()) {
            return;
        }

        const existingKeywords = savedKeywords.current.map((keyword) => keyword.value);

        const trimValue = queryKeyword.trim();

        if (existingKeywords.includes(trimValue) || existingKeywords.length >= keywordsLimit) {
            return;
        }

        const updatedKeywords = [{ value: trimValue, isSelected: false }, ...savedKeywords.current];

        setKeywordList(updatedKeywords);
        updateSavedKeywords(updatedKeywords);
        setQueryKeyword("");

        if (!title) {
            onChangeTitle(trimValue);
        }

        track.trackAddNewKeyword(AddedKWType.TYPING);
    };

    const selectKWFromSearch = () => {
        const result = [];

        keywordsList.forEach((keyword) => {
            result.push({ ...keyword, isSelected: true });
            selectedKeywords.current.push(keyword.value);
        });

        setKeywordList(result);
    };

    const removeKeywords = (index, removeAllSelected) => {
        removedKeywordsList.current = savedKeywords.current;

        if (removeAllSelected) {
            const filteredSavedKW = savedKeywords.current.filter(
                (keyword) => !selectedKeywords.current.includes(keyword.value),
            );
            const filteredList = keywordsList.filter((item) => !item.isSelected);

            updateSavedKeywords(filteredSavedKW);

            setKeywordList(filteredList);
        } else {
            const filteredList = keywordsList.filter((_item, i) => i !== index);

            updateSavedKeywords(filteredList);
            setKeywordList(filteredList);
        }
    };

    return {
        keywordsList,
        savedKeywords,
        selectedKeywords,
        removedKeywordsList,
        queryKeyword,
        title,
        titleError,
        keywordsGroups,
        updateKeywordsList,
        showToast,
        setKeywordList,
        updateSavedKeywords,
        selectKWFromSearch,
        clearSelection,
        setQueryKeyword,
        onAdd,
        onChangeTitle,
        removeKeywords,
    };
};

export default useKWModalUpdater;
